import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { ClerkProvider } from "@clerk/clerk-react";
import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { toast } from "sonner";

import { Toaster } from "~/components/ui/sonner";
import { errorMessage, isNotFound, isNotStaff, isUnauthenticated } from "~/lib/api";
import { clerkAppearance } from "~/lib/clerk-appearance";
import { applyTheme, readStoredTheme } from "~/lib/theme";
import { routeTree } from "./routeTree.gen";
import "./styles.css";

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!PUBLISHABLE_KEY) {
  // Fail loudly rather than rendering a blank page nobody can diagnose.
  console.warn("VITE_CLERK_PUBLISHABLE_KEY is not set — Clerk will not initialize.");
}

// index.html already set the class before first paint; this keeps the two in
// agreement if the stored value changed in another tab.
applyTheme(readStoredTheme());

const queryClient = new QueryClient({
  // One place to surface a failed write, so no page repeats it. The three
  // errors that have a dedicated screen are excluded: 401 and 403 are handled
  // by the _authed gate, and 404 is an empty state on the page that asked.
  // Toasting those as well would put a red box on top of the right answer.
  //
  // A mutation can opt out entirely with meta.silenceErrorToast when it renders
  // its own failure in place -- a field-level validation or conflict message is
  // more useful than a toast, and showing both says less, louder.
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.silenceErrorToast) return;
      if (isUnauthenticated(error) || isNotStaff(error) || isNotFound(error)) return;
      toast.error(errorMessage(error));
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root not found");

ReactDOM.createRoot(rootEl).render(
  <StrictMode>
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY ?? ""}
      afterSignOutUrl="/sign-in"
      appearance={clerkAppearance}
    >
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <Toaster richColors closeButton position="top-right" />
      </QueryClientProvider>
    </ClerkProvider>
  </StrictMode>,
);
